import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sparePartsService } from '../../services/sparePartsService';
import { maintPlanService } from '../../services/maintPlanService';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { ProductPickerModal } from './ProductPickerModal';

export const PlansSpareParts = ({ planId }) => {
    const [newSparePart, setNewSparePart] = useState({
        product_id: '',
        quantity_required: '',
        unit: '',
    });

    const [editFormData, setEditFormData] = useState({
        product_id: '',
        quantity_required: '',
        unit: ''
    });

    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        setNewSparePart(prev => ({
            ...prev,
            product_id: selectedProduct ? selectedProduct.id : ''
        }));

    }, [selectedProduct]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [localModalError, setLocalModalError] = useState(null);
    const queryClient = useQueryClient();

    //Fetch Maintenance Plan to read Spare Part List
    const { data: plans = [], isLoading: sparePartsLoading, error: sparePartsError } = useQuery({
        queryKey: ['procedures-and-spare-parts', planId],
        queryFn: () => maintPlanService.getById(planId),
        enabled: !!planId,
    });
    //Retrieve Spare Parts
    const spareParts = plans.required_spare_parts || [];

    //Form Validation
    const validateForm = (form) => {
        const errors = {};

        if (!form.product_id) {
            errors.product_id = 'Product is required';
        }

        if (!form.quantity_required.toString().trim()) {
            errors.quantity_required = 'Quantity is required';
        } else if (isNaN(form.quantity_required) || parseFloat(form.quantity_required) <= 0) {
            errors.quantity_required = 'Quantity must be a positive number';
        } else if (form.quantity_required.toString().split('.')[1]?.length > 3) {
            errors.quantity_required = 'Quantity must not have more than 3 decimal places';
        } else if (parseFloat(form.quantity_required) > 9999999.999) {
            errors.quantity_required = 'Quantity must be less than or equal to 9,999,999.999';
        } else if (form.quantity_required.toString().includes('.') && form.unit.trim() === 'units') {
            errors.quantity_required = 'Quantity cannot have decimals when unit is "units"';
        }

        if (!form.unit.trim()) {
            errors.unit = 'Unit is required';
        } else if (form.unit.trim().length > 50) {
            errors.unit = 'Unit must be less than 50 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    //Create Spare Part
    const createSparePartMutation = useMutation({
        mutationFn: (sparePartData) => sparePartsService.create(planId, sparePartData),
        onSuccess: () => {
            //Refresh
            queryClient.invalidateQueries({ queryKey: ['procedures-and-spare-parts', planId] });
            setNewSparePart({
                product_id: '',
                quantity_required: '',
                unit: ''
            });
            setFormErrors({});
            setLocalModalError(null);
            setIsSubmitting(false);
        },
        onError: (error) => {
            console.error('Error creating spare part:', error);
            setLocalModalError(error.message || 'Error creating spare part');
            setIsSubmitting(false);
        },
    });

    // const updateSparePartMutation = useMutation({
    //     mutationFn: ({ id, data }) => sparePartsService.update(id, data),
    //     onSuccess: () => {
    //         queryClient.invalidateQueries({ queryKey: ['procedures-and-spare-parts', planId] });
    //         setEditingSparePartId(null);
    //         setEditFormData({
    //             product_id: '',
    //             quantity_required: '',
    //             unit: ''
    //         });
    //     },
    //     onError: (error) => {
    //         console.error('Error updating spare part:', error);
    //     },
    // });

    // Delete spare part mutation
    const deleteSparePartMutation = useMutation({
        mutationFn: (sparePartId) => sparePartsService.delete(sparePartId),
        onSuccess: () => {
            //Refresh
            queryClient.invalidateQueries({ queryKey: ['procedures-and-spare-parts', planId] });
        },
        onError: (error) => {
            console.error('Error deleting spare part:', error);
            setLocalModalError(error.message || 'Error deleting spare part');
            setTimeout(() => setLocalModalError(null), 3000);
        },
    });

    //Handles
    const handleAddSparePart = async () => {
        if (!validateForm(newSparePart)) {
            return;
        }

        setIsSubmitting(true);
        setLocalModalError(null);

        createSparePartMutation.mutate({
            product_id: newSparePart.product_id || 0,
            quantity_required: parseFloat(newSparePart.quantity_required) || 0,
            unit: newSparePart.unit.trim()
        });
    };

    const handleDeleteSparePart = (sparePartId) => {
        if (window.confirm('Are you sure you want to delete this spare part?')) {
            deleteSparePartMutation.mutate(sparePartId);
        }
    };

    //Retrieve product from picker modal
    const handleSelectProduct = (product) => {
        setSelectedProduct(product);
    };

    //Update input per key press, selected unit shouldn't exceed 50 characters.
    const handleSparePartInputChange = (e) => {
        const { name, value } = e.target;
        setNewSparePart(prev => ({
            ...prev,
            [name]: name === 'unit' ? value.slice(0, 50) : value
        }));
    }

    //Open picker modal
    const modalProductTable = () => {
        setIsPickerOpen(true);
    }

    //Loading screen
    if (sparePartsLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-2">
            {/* Product Selection Modal */}
            {isPickerOpen && createPortal(
                <ProductPickerModal
                    isOpen={isPickerOpen}
                    onClose={() => setIsPickerOpen(false)}
                    onSelect={handleSelectProduct}
                    initialProductId={selectedProduct?.id || null}
                />, document.body)
            }

            <h3 className="text-lg font-medium text-white">Required Spare Parts</h3>
            <div>
                <div className="flex pt-2">
                    <div className="text-white flex-1 font-semibold grid grid-cols-2 pb-2 gap-4">
                        <p className="ml-4">Product</p>
                        <p className="">Quantity</p>
                    </div>
                    {/* Invisible delete button to preserve alignment*/}
                    <button
                        className="ml-4 border invisible border-green-500 p-1 rounded hover:bg-red-600/50 text-gray-400 hover:text-red-400 transition-colors duration-200"
                        onClick={() => { }}
                        title="Delete spare part"
                        type="button"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="border-t border-gray-600 w-full h-0"></div>
            </div>
            <div className="grid grid-cols-1 gap-2">
                {spareParts.length > 0 ? (
                    spareParts.map((sparepart) => (
                        <div className="bg-gray-700/50 rounded-lg px-4 py-2 overflow-auto hover:bg-gray-700/70 transition-colors" key={sparepart.id}>
                            <div className="flex">
                                <div className="grid grid-cols-2 flex-1 gap-4">
                                    <p className="text-white text-gray-400 mb-1">{sparepart?.product?.Name || 'N/A'}</p>
                                    <p className="text-white font-medium overflow-auto mb-1 ">{`${sparepart?.quantity_required || 'N/A'} ${sparepart?.unit || 'N/A'}`}</p>
                                </div>

                                <button
                                    className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => handleDeleteSparePart(sparepart.id)}
                                    title="Delete spare part"
                                    type="button"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="bg-gray-700/50 rounded-lg px-4 py-2 overflow-auto hover:bg-gray-700/70 transition-colors">
                        <p className="text-gray-400 mb-1">No spare parts added yet.</p>
                    </div>
                )}

                <div className="border-t border-gray-700 pt-2">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Plus className="w-5 h-5 text-blue-400" />
                        Add New Spare Part
                    </h3>
                    <button
                        type="button"
                        onClick={modalProductTable}
                        className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                        Products
                    </button>

                    <div className="space-y-2">
                        {/* Product */}
                        <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-300 mb-2 flex-shrink-0">
                                Product <span className="text-red-400">*</span>
                            </label>
                            {/* No longer input */}
                            <label
                                className={`w-full px-4 py-2 rounded-lg border bg-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.product_id ? 'border-red-500' : 'border-gray-600'
                                    } ${newSparePart.product_id == '' ? 'text-gray-400' : 'text-white'}`}
                            >
                                {newSparePart.product_id !== '' ? selectedProduct?.name : (<p>Press the <strong>Products</strong> button to select a product</p>)}
                            </label>
                            {formErrors.product_id && (
                                <p className="mt-1 text-sm text-red-400">{formErrors.product_id}</p>
                            )}
                        </div>

                        {/* Quantity and Unit */}
                        <div className="flex flex-row gap-2 items-start">
                            <div className="flex-col w-1/3">
                                <label className="block text-sm font-medium text-gray-300 mb-2 flex-shrink-0">
                                    Quantity: <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="quantity_required"
                                    min="0.001"
                                    max="9999999.999"
                                    step="0.001"
                                    value={newSparePart.quantity_required}
                                    onChange={handleSparePartInputChange}
                                    placeholder="1"
                                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.quantity_required ? 'border-red-500' : 'border-gray-600'
                                        }`}
                                    disabled={isSubmitting}
                                />
                                {formErrors.quantity_required && (
                                    <p className="mt-1 text-sm text-red-400">{formErrors.quantity_required}</p>
                                )}
                            </div>
                            <div className="flex-col w-1/3">
                                <label className="block text-sm font-medium text-gray-300 mb-2 flex-shrink-0">
                                    Unit: <span className="text-red-400">*</span>
                                </label>
                                <select
                                    type="text"
                                    name="unit"
                                    value={newSparePart.unit}
                                    onChange={handleSparePartInputChange}
                                    placeholder="Enter unit (e.g., pcs, kg)"
                                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.unit ? 'border-red-500' : 'border-gray-600'
                                        }`}
                                    disabled={isSubmitting}>
                                    <option selected disabled value="">
                                        Select a unit
                                    </option>
                                    <option value="units">Units</option>
                                    <option value="kg">kg</option>
                                    <option value="g">grams</option>
                                    <option value="mg">mg</option>
                                    <option value="l">liters</option>
                                    <option value="ml">ml</option>
                                </select>

                                {formErrors.unit && (
                                    <p className="mt-1 text-sm text-red-400">{formErrors.unit}</p>
                                )}
                            </div>
                            <div>
                                {/* Invisible label to align the add button */}
                                <label className="block text-sm invisible font-medium text-gray-300 mb-2 flex-shrink-0">
                                    <span className="text-red-400">*</span>
                                </label>
                                <div className="">
                                    <button
                                        type="button"
                                        onClick={handleAddSparePart}
                                        disabled={isSubmitting || !selectedProduct}
                                        className="flex items-center justify-center w-full px-6 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                Adding...
                                            </>
                                        ) : (
                                            <>
                                                <Plus className="w-4 h-4 mr-2" />
                                                <span className='whitespace-nowrap'>Add Spare Part</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Error message */}
            {localModalError && (
                <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                    <p className="text-red-300 text-sm">{localModalError}</p>
                </div>
            )}
        </div>
    );
};